import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import type { PublicFormView } from '@cloudsign/shared';
import { Alert, Button, CheckboxField, SelectField, TextField } from '../../components/ui';
import { addDays, localToUtcIso, todayIn } from '../../lib/zoneTime';
import { problemText } from '../public-links/api';
import { LinkProblem, PageLoading, PublicShell, Section } from '../public-links/PublicShell';
import { getForm, submitForm } from './api';
import { useVisitorLanguage } from './locale';

type Question = PublicFormView['questions'][number];
type Answer = string | boolean | string[];

/** Public pre-registration form: /:tenantSlug/forms/:slug (docs/modules/pre-registration-phase4.md s2). */
export function FormPage() {
  const { tenantSlug = '', slug = '' } = useParams();
  const q = useQuery({ queryKey: ['public-p4', 'form', tenantSlug, slug], queryFn: () => getForm(tenantSlug, slug), retry: false });
  return (
    <PublicShell slug={tenantSlug} {...(q.data ? { title: q.data.groupName } : {})}>
      {q.isPending ? <PageLoading /> : q.isError ? <LinkProblem error={q.error} onRetry={() => void q.refetch()} /> : <FormBody view={q.data} tenantSlug={tenantSlug} slug={slug} />}
    </PublicShell>
  );
}

function FormBody({ view, tenantSlug, slug }: { view: PublicFormView; tenantSlug: string; slug: string }) {
  const { vt } = useVisitorLanguage();
  const today = todayIn(view.siteTimeZone);
  const [v, setV] = useState({ name: '', email: '', phone: '', company: '', hostName: '', date: today, time: '09:00', website: '' });
  const [answers, setAnswers] = useState<Record<string, Answer>>({});
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = (k: keyof typeof v) => (e: { target: { value: string } }) => setV((p) => ({ ...p, [k]: e.target.value }));

  const send = useMutation({
    mutationFn: () =>
      submitForm(tenantSlug, slug, {
        name: v.name.trim(),
        email: v.email.trim() || null,
        phone: v.phone.trim() || null,
        company: v.company.trim() || null,
        hostName: v.hostName.trim() || null,
        expectedAt: localToUtcIso(v.date, v.time, view.siteTimeZone),
        answers: Object.fromEntries(Object.entries(answers).filter(([, a]) => a !== '' && !(Array.isArray(a) && a.length === 0)).map(([id, a]) => [id, view.questions.find((x) => x.id === id)?.type === 'number' ? Number(a) : a])),
        consent,
        website: v.website,
      }),
  });

  if (send.isSuccess) {
    return (
      <Section title={vt('forms.public.thanks.title')}>
        <Alert kind="success">{send.data.thanks || vt(send.data.status === 'pending' ? 'forms.public.thanks.pending' : 'forms.public.thanks.approved')}</Alert>
      </Section>
    );
  }

  const need = (k: string, bad: boolean, into: Record<string, string>) => {
    if (bad) into[k] = vt('public.required');
  };
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const bad: Record<string, string> = {};
    need('name', v.name.trim() === '', bad);
    need('email', view.fields.email === 'required' && v.email.trim() === '', bad);
    need('phone', view.fields.phone === 'required' && v.phone.trim() === '', bad);
    need('company', view.fields.company === 'required' && v.company.trim() === '', bad);
    need('host', view.fields.host === 'required' && v.hostName.trim() === '', bad);
    need('date', v.date === '' || v.time === '', bad);
    need('consent', view.consentText !== '' && !consent, bad);
    for (const qn of view.questions) need(qn.id, qn.required && (answers[qn.id] === undefined || answers[qn.id] === '' || (Array.isArray(answers[qn.id]) && (answers[qn.id] as string[]).length === 0)), bad);
    setErrors(bad);
    if (Object.keys(bad).length === 0) send.mutate();
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-4">
      <Section>
        <p className="text-sm text-slate-700">{vt('forms.public.siteLine', { site: view.siteName, tenant: view.tenantName })}</p>
        {view.intro ? <p className="whitespace-pre-line">{view.intro}</p> : null}
      </Section>
      <Section>
        <TextField label={vt('forms.public.name')} value={v.name} onChange={set('name')} autoComplete="name" required maxLength={120} error={errors['name']} />
        <TextField label={vt('forms.public.email')} type="email" value={v.email} onChange={set('email')} autoComplete="email" required={view.fields.email === 'required'} error={errors['email']} />
        {view.fields.phone !== 'off' ? (
          <TextField label={vt('forms.public.phone')} type="tel" value={v.phone} onChange={set('phone')} autoComplete="tel" required={view.fields.phone === 'required'} error={errors['phone']} />
        ) : null}
        {view.fields.company !== 'off' ? (
          <TextField label={vt('forms.public.company')} value={v.company} onChange={set('company')} autoComplete="organization" required={view.fields.company === 'required'} maxLength={120} error={errors['company']} />
        ) : null}
        {view.fields.host !== 'none' ? (
          <TextField label={vt('forms.public.host')} hint={vt('forms.public.hostHint')} value={v.hostName} onChange={set('hostName')} required={view.fields.host === 'required'} maxLength={120} error={errors['host']} />
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label={vt('forms.public.date')}
            type="date"
            value={v.date}
            min={today}
            max={addDays(today, view.maxDaysAhead)}
            onChange={set('date')}
            required
            hint={vt('forms.public.dateHint', { days: view.maxDaysAhead, site: view.siteName })}
            error={errors['date']}
          />
          <TextField label={vt('forms.public.time')} type="time" value={v.time} onChange={set('time')} required />
        </div>
      </Section>

      {view.questions.length > 0 ? (
        <Section title={vt('forms.public.questions')}>
          {view.questions.map((qn) => (
            <QuestionInput key={qn.id} q={qn} value={answers[qn.id]} error={errors[qn.id]} onChange={(a) => setAnswers((p) => ({ ...p, [qn.id]: a }))} />
          ))}
        </Section>
      ) : null}

      {view.consentText ? (
        <Section>
          <CheckboxField label={view.consentText} checked={consent} onChange={(e) => setConsent(e.target.checked)} />
          {errors['consent'] ? <p className="text-sm text-red-700">{errors['consent']}</p> : null}
        </Section>
      ) : null}

      {/* Honeypot: people never see or reach it; the server accepts a filled one silently and keeps nothing. */}
      <div aria-hidden="true" style={{ position: 'absolute', left: '-10000px', width: 1, height: 1, overflow: 'hidden' }}>
        <label>
          {vt('forms.public.honeypot')}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" value={v.website} onChange={set('website')} />
        </label>
      </div>

      {send.isError ? <Alert>{problemText(send.error)}</Alert> : null}
      <Button type="submit" variant="primary" className="w-full" disabled={send.isPending}>
        {send.isPending ? vt('forms.public.submitting') : vt('forms.public.submit')}
      </Button>
      {view.privacyUrl ? (
        <p className="text-sm">
          <a href={view.privacyUrl} target="_blank" rel="noopener noreferrer" className="underline">
            {vt('forms.public.privacy')}
          </a>
        </p>
      ) : null}
    </form>
  );
}

function QuestionInput({ q, value, error, onChange }: { q: Question; value: Answer | undefined; error: string | undefined; onChange: (a: Answer) => void }) {
  const { vt } = useVisitorLanguage();
  const label = q.label;
  if (q.type === 'yes_no' || q.type === 'acknowledge') {
    return (
      <div>
        <CheckboxField label={label} checked={value === true} onChange={(e) => onChange(e.target.checked)} />
        {error ? <p className="mt-1 text-sm text-red-700">{error}</p> : null}
      </div>
    );
  }
  if (q.type === 'multi_select' && q.options.length > 0) {
    const chosen = Array.isArray(value) ? value : [];
    return (
      <fieldset className="space-y-2" aria-invalid={error ? true : undefined}>
        <legend className="text-sm font-medium">
          {label}
          {q.required ? <span aria-hidden="true"> *</span> : null}
        </legend>
        {q.options.map((o) => (
          <CheckboxField key={o} label={o} checked={chosen.includes(o)} onChange={(e) => onChange(e.target.checked ? [...chosen, o] : chosen.filter((x) => x !== o))} />
        ))}
        {error ? <p className="text-sm text-red-700">{error}</p> : null}
      </fieldset>
    );
  }
  if (q.type === 'select' && q.options.length > 0) {
    return (
      <SelectField label={label} value={typeof value === 'string' ? value : ''} onChange={(e) => onChange(e.target.value)} required={q.required} error={error}>
        <option value="">{vt('public.choose')}</option>
        {q.options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </SelectField>
    );
  }
  return (
    <TextField
      label={label}
      type={q.type === 'number' ? 'number' : 'text'}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(e.target.value)}
      required={q.required}
      error={error}
    />
  );
}
